import Box from '@mui/material/Box';
import {
  ModelError_Item,
  ModelError_Response,
  postV1CompaniesCompanyIdRatesRateIdCopy,
  Rate_Copy,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Account } from '~hooks/useAccount/models';
import { Rate } from '~hooks/useRates/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import RateCopyToForm, { RateCopyToFormHandler } from './RateCopyToForm/RateCopyToForm';

interface CopyRateDialogProps {
  accountOptions: Account[];
  isOpen: boolean;
  onClose: () => void;
  rate?: Rate;
}

export function CopyRateDialog({
  accountOptions,
  isOpen,
  onClose,
  rate,
}: CopyRateDialogProps) {
  const rateId = rate?.id;
  const rateCopyToFormHandler = useRef<RateCopyToFormHandler>(null);

  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>();

  const handleSubmit = useCallback(async () => {
    const data = await rateCopyToFormHandler.current?.onSubmit();

    if (data && rateId) {
      try {
        setAsyncErrors(undefined);
        setIsSubmitting(true);

        const body: Rate_Copy = {
          account_id: data.account.value,
          equipment_ids: data.equipments?.map((equipment) => equipment.value),
          name: data.name,
          project_ids: data.projects?.map((project) => project.value),
          service_ids: data.services?.map((service) => service.value),
        };

        await postV1CompaniesCompanyIdRatesRateIdCopy({
          path: { 'company-id': companyId, 'rate-id': rateId },
          body,
        });
        toasterStore.push(
          alert(t('administration.rates.rate_copied'), AlertTypes.success),
        );
        setIsSubmitting(false);
        onClose();
      } catch (error) {
        setIsSubmitting(false);

        if (isAxiosError(error) && error.response?.status === 422) {
          const e = error as AxiosError<ModelError_Response>;

          setAsyncErrors(e.response?.data.error.errors || []);
        } else {
          connection.handleRequestError(error, t('error_messages.rates.failed_to_copy'));
        }
      }
    }
  }, [companyId, rateId, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setAsyncErrors(undefined);
    }
  }, [isOpen]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <OverflowAwareText sx={{ fontSize: '16px', fontWeight: 600 }}>
            {t('administration.rates.copy_rate')}
          </OverflowAwareText>

          <OverflowAwareText>
            {t('administration.rates.rate_where_to_copy')}
          </OverflowAwareText>
        </Box>
      </DialogHeader>

      <DialogContent>
        {rate ? (
          <RateCopyToForm
            ref={rateCopyToFormHandler}
            availableAccounts={accountOptions}
            errors={asyncErrors}
            rate={rate}
          />
        ) : null}
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSubmitting}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton loading={isSubmitting} onClick={handleSubmit}>
          {t('actions.copy')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
